import { X, Zap, AlertTriangle } from 'lucide-react';
import {
  estimateGenerationCost,
  getCompositionBreakdown,
  BLOCK_META,
  AI_MODELS,
} from '@ellie/shared';
import type { Order, WorkbookProfile, BlockType, PaceCode, AIProviderCode } from '@ellie/shared';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CostIndicatorModalProps {
  order: Order;
  profile: WorkbookProfile;
  onConfirm: () => void;
  onCancel: () => void;
  isRegenerating: boolean;
  /** Active AI provider — defaults to Anthropic if omitted. */
  provider?: AIProviderCode;
  /** Active model ID — defaults to Sonnet if omitted. */
  model?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format cents to a currency string (e.g. 2495 EUR -> "EUR 24.95"). */
function formatCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/** Rough USD -> EUR conversion for display purposes. */
const USD_TO_EUR = 0.92;

function formatUsdAsEur(usd: number): string {
  const eur = usd * USD_TO_EUR;
  return new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency: 'EUR',
  }).format(eur);
}

function formatUsd(usd: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(usd);
}

/** Look up a human-readable model name from AI_MODELS, or fall back to the raw ID. */
function getModelDisplayName(provider: AIProviderCode, model: string): string {
  const models = AI_MODELS[provider];
  return models?.find((m) => m.id === model)?.name ?? model;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CostIndicatorModal({
  order,
  profile,
  onConfirm,
  onCancel,
  isRegenerating,
  provider,
  model,
}: CostIndicatorModalProps) {
  const estimate = estimateGenerationCost(profile.pace as PaceCode, {
    provider,
    model,
  });
  const breakdown = getCompositionBreakdown(profile.pace as PaceCode);

  const revenueCents = order.amount_cents;
  const revenueEur = revenueCents / 100;
  const totalCostEur = estimate.totalEstimatedCostUsd * USD_TO_EUR;
  const marginEur = revenueEur - totalCostEur;
  const marginPositive = marginEur >= 0;

  const action = isRegenerating ? 'Regenerate' : 'Generate';
  const modelName = getModelDisplayName(estimate.provider, estimate.model);
  const renderModelName = getModelDisplayName('anthropic', estimate.renderModel);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />

      {/* Modal */}
      <div className="relative w-full max-w-md rounded-xl border border-border bg-white p-6 shadow-xl">
        {/* Close button */}
        <button
          onClick={onCancel}
          className="absolute right-3 top-3 rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-text-primary"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <h2 className="mb-4 text-lg font-semibold text-text-primary">{action} Workbook</h2>

        {/* Cost breakdown */}
        <div className="mb-4 space-y-3 rounded-lg bg-gray-50 p-4">
          {/* Revenue */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-muted">Customer paid</span>
            <span className="font-medium text-text-primary">
              {formatCurrency(revenueCents, order.currency)}
            </span>
          </div>

          {/* AI generation cost */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-muted">
              Est. generation ({modelName})
              <span className="ml-1 text-xs text-text-muted/70">
                ({formatUsd(estimate.estimatedCostUsd)})
              </span>
            </span>
            <span className="font-medium text-text-primary">
              {formatUsdAsEur(estimate.estimatedCostUsd)}
            </span>
          </div>

          {/* HTML rendering cost */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-muted">
              Est. rendering ({renderModelName})
              <span className="ml-1 text-xs text-text-muted/70">
                ({formatUsd(estimate.renderEstimatedCostUsd)})
              </span>
            </span>
            <span className="font-medium text-text-primary">
              {formatUsdAsEur(estimate.renderEstimatedCostUsd)}
            </span>
          </div>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Total AI cost */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-muted">
              Total AI cost
              <span className="ml-1 text-xs text-text-muted/70">
                ({formatUsd(estimate.totalEstimatedCostUsd)})
              </span>
            </span>
            <span className="font-medium text-text-primary">
              {formatUsdAsEur(estimate.totalEstimatedCostUsd)}
            </span>
          </div>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Margin */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-text-muted">Margin</span>
            <span className={`font-semibold ${marginPositive ? 'text-green-600' : 'text-red-600'}`}>
              {marginPositive ? '+' : ''}
              {formatCurrency(Math.round(marginEur * 100), 'EUR')}
            </span>
          </div>

          {!marginPositive && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 p-2 text-xs text-red-700">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Estimated AI cost exceeds customer revenue.</span>
            </div>
          )}
        </div>

        {/* Block breakdown */}
        <div className="mb-5">
          <h3 className="mb-2 text-sm font-medium text-text-primary">
            Block breakdown
            <span className="ml-1 font-normal text-text-muted">
              ({estimate.blockCount} blocks, ~{estimate.estimatedPages} pages)
            </span>
          </h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {(Object.entries(breakdown) as [BlockType, number][])
              .filter(([, count]) => count > 0)
              .map(([type, count]) => {
                const meta = BLOCK_META[type];
                return (
                  <div key={type} className="flex items-center justify-between text-xs">
                    <span className="text-text-muted">{meta.name}</span>
                    <span className="font-mono text-text-primary">x{count}</span>
                  </div>
                );
              })}
          </div>
          <p className="mt-1 text-xs text-text-muted">
            + verb glossary & cultural notes (back matter)
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700"
          >
            <Zap className="h-3.5 w-3.5" />
            {action}
          </button>
        </div>
      </div>
    </div>
  );
}
